<template>
  <LModal
    :visible="modelValue"
    size="md"
    :title="title"
    form-class="w-100 h-100"
    ok-variant="secondary"
    :submit="
      async () => {
        await setPriority(EisenhowerPriority.NO_PRIORITY);
        return { success: true };
      }
    "
    @update:visible="$emit('update:modelValue', $event)"
  >
    <template #default>
      <div class="eisenhower-grid">
        <span />
        <span>
          {{ t("components.event-action.event.eisenhowerMatrix.EisenhowerMatrixModal.urgent") }}
        </span>
        <span>
          {{ t("components.event-action.event.eisenhowerMatrix.EisenhowerMatrixModal.notUrgent") }}
        </span>
        <span class="text-vertical">
          {{
            t("components.event-action.event.eisenhowerMatrix.EisenhowerMatrixModal.importantText")
          }}
        </span>

        <div
          class="col-5 lisaui:flex lisaui:items-center lisaui:justify-center matrix-index Eisenhower-DO"
          :class="{ highlight: priority === EisenhowerPriority.DO }"
          data-testid="Eisenhower-DO"
          @click="setPriority(EisenhowerPriority.DO)"
        >
          <div class="lisaui:text-center">
            <div>
              <i class="fa-solid fa-bolt"></i>
            </div>
            <div>
              <strong>{{ t(`components.eventAction.filter.badges.PriorityFilter.DO`) }}</strong>
            </div>
          </div>
        </div>
        <div
          class="col-5 lisaui:flex lisaui:items-center lisaui:justify-center matrix-index Eisenhower-SCHEDULE"
          :class="{ highlight: priority === EisenhowerPriority.SCHEDULE }"
          data-testid="Eisenhower-SCHEDULE"
          @click="setPriority(EisenhowerPriority.SCHEDULE)"
        >
          <div class="lisaui:pt-2 lisaui:pb-2 lisaui:text-center">
            <div>
              <i class="fas fa-calendar"></i>
            </div>
            <div>
              <strong>{{
                t(`components.eventAction.filter.badges.PriorityFilter.SCHEDULE`)
              }}</strong>
            </div>
          </div>
        </div>

        <span class="text-vertical">
          {{
            t("components.event-action.event.eisenhowerMatrix.EisenhowerMatrixModal.notImportant")
          }}
        </span>
        <div
          class="col-5 lisaui:flex lisaui:items-center lisaui:justify-center matrix-index Eisenhower-DELEGATE"
          :class="{ highlight: priority === EisenhowerPriority.DELEGATE }"
          data-testid="Eisenhower-DELEGATE"
          @click="setPriority(EisenhowerPriority.DELEGATE)"
        >
          <div class="lisaui:text-center">
            <div>
              <i class="fas fa-user-plus"></i>
            </div>
            <div>
              <strong>{{
                t(`components.eventAction.filter.badges.PriorityFilter.DELEGATE`)
              }}</strong>
            </div>
          </div>
        </div>
        <div
          class="col-5 lisaui:flex lisaui:items-center lisaui:justify-center matrix-index Eisenhower-DELETE"
          :class="{ highlight: priority === EisenhowerPriority.DELETE }"
          data-testid="Eisenhower-DELETE"
          @click="setPriority(EisenhowerPriority.DELETE)"
        >
          <div class="lisaui:text-center">
            <div>
              <i class="fas fa-trash-alt"></i>
            </div>
            <div>
              <strong>{{ t(`components.eventAction.filter.badges.PriorityFilter.DELETE`) }}</strong>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #modal-ok>
      <i class="fa-solid fa-ban mr-2" />
      {{ t("components.eventAction.filter.badges.PriorityFilter.noPriorityButton") }}
    </template>
  </LModal>
</template>

<script setup lang="ts">
import { computed, type PropType } from "vue";
import LModal from "@/components/LModal.vue";
import { EisenhowerPriority, useSQDCEventEisenhowerPriorityUpdateMutation } from "@/graphql";
import { useTranslation } from "@/language/translation";

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  event: {
    type: Object as PropType<{
      id: number;
      typeId: number;
    }>,
    required: false,
    default: undefined,
  },
  eventPrefix: {
    type: String,
    required: false,
    default: undefined,
  },
});

const priority = defineModel("priority", {
  type: String as PropType<EisenhowerPriority>,
  required: true,
});

const emit = defineEmits(["update:modelValue"]);

const { t } = useTranslation();

const title = computed(() => {
  const titleValue = t(
    "components.event-action.event.eisenhowerMatrix.EisenhowerMatrixModal.eisenhowerMatrixTitle",
  );
  if (props.event == null) {
    return titleValue;
  }
  return `${titleValue} - ${props.eventPrefix}${props.event.id}`;
});

const { mutate: updateEventEisenhowerPriority } = useSQDCEventEisenhowerPriorityUpdateMutation({});

async function setPriority(priorityValue: EisenhowerPriority) {
  if (props.event != null) {
    await updateEventEisenhowerPriority({
      eventId: props.event.id,
      priority: priorityValue,
    });
  }
  priority.value = priorityValue;
  emit("update:modelValue", false);
}
</script>

<style lang="scss" scoped>
.text-vertical {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
}
.matrix-index {
  cursor: pointer;
  position: relative;
  transition:
    z-index 0.3s,
    transform 0.3s;
}
.matrix-index:hover {
  z-index: 1;
  transform: scale(1.05);
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
}

.Eisenhower-DO {
  border-top-left-radius: 3px;
}
.Eisenhower-SCHEDULE {
  border-top-right-radius: 3px;
}
.Eisenhower-DELEGATE {
  border-bottom-left-radius: 3px;
}
.Eisenhower-DELETE {
  border-bottom-right-radius: 3px;
}

.highlight {
  outline: 2px solid black;
}

.eisenhower-grid {
  display: grid;
  grid-template-columns: auto 1fr 1fr;
  grid-template-rows: auto 1fr 1fr;
  gap: 0.5rem;
  padding: 1rem;
  width: 100%;
  height: 100%;
  box-sizing: border-box;

  > * {
    min-width: 0;
    min-height: 0;
  }

  > span {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  > div {
    aspect-ratio: 1 / 1;
    width: 100%;
    height: 100%;
    overflow: hidden;
    object-fit: cover;
  }
}
</style>
